/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";

import { FC, useEffect, useRef, useState } from "react";
import { addCertificate } from "../core/_requests";
import { useAuth } from "../../../auth";

import { Cerificate, initialValues } from "../core/_models";
import clsx from "clsx";
import { toast } from "react-toastify";
import { certificateSchema } from "../core/Schema";

const AddCertificate: FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Cerificate>(initialValues);
  const [errorMessage, setErrorMessage] = useState("");
  const [formImages, setFormImages] = useState<File[]>([]);
  const chefId = currentUser?.uuid;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const clickCancel = () => {
    navigate("/settings");
  };

  // Formik configuration
  const formik = useFormik<Cerificate>({
    initialValues,
    validationSchema: certificateSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      const updatedData = Object.assign(data, values);
      values.chef_id = chefId;
      const formData = new FormData();

      // Ensure files are valid and append them
      if (formImages) {
        formImages.forEach((file) => {
          formData.append("certificate", file);
        });
      }

      // Handle undefined values by ensuring they are strings
      formData.append("name", values.name ?? "");
      formData.append("description", values.description ?? "");
      formData.append("chef_id", values.chef_id ?? "");

      // Call your API
      addCertificate(formData).then((res) => {
        setLoading(false);
        if (res.status === true) {
          navigate("/settings");
          toast.success(res?.message, { autoClose: 5000 });
        } else {
          toast.error(res?.message, { autoClose: 5000 });
          // Redirect to the settings page
          navigate("/settings");
        }
      });
    },
  });

  // Handling file upload with validation
  const imageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/webp",
      "application/pdf",
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    ];

    if (files) {
      const newFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        if (!allowedTypes.includes(files[i].type)) {
          setErrorMessage(
            "Invalid file type. Allowed types: JPEG, PNG, WEBP, PDF, DOC, DOCX."
          );
          return;
        } else if (files[i].size > 1024 * 1024) {
          setErrorMessage("File size must be less than 1MB.");
          return;
        }
        newFiles.push(files[i]);
      }
      setErrorMessage("");
      setFormImages([...formImages, ...newFiles]); // Append new files to the existing list
    }
  };

  // Removing a file from the uploaded list
  const removeFile = (indexToRemove: number) => {
    setFormImages((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleErrorMessageClose = () => {
    setErrorMessage("");
  };
  useEffect(() => {
    const emptyValues: Cerificate = {
      primaryId: "",
      name: "",
      chef_id: "",
      description: "",
      certificate: "",
      status: 0,
      created_by: 0,
      updated_by: 0,
    };
    formik.setValues(emptyValues);
      }, []);
  return (
    <section className="container-fluid">
      <div className="row">
        <section className="content-section">
          <div className="overlay"></div>
          <div className="drawer small">
            <div className="title row">
              <h3 className="col-10 mb-0">Add Certificate</h3>
              <div className="col-2 text-end mb-2">
                <button
                  className="btn p-0 close d-inline-flex"
                  onClick={clickCancel}
                >
                  <i className="material-symbols-outlined">close</i>
                </button>
              </div>
              <h6>Upload your course completion certificate here!</h6>
            </div>
            <form
              onSubmit={formik.handleSubmit}
              noValidate
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            >
              <div className="col-12 mb-3 mt-4">
                <label htmlFor="name" className="form-label">
                  Certificate name / title
                </label>
                <input
                  type="text"
                  className={clsx("form-control", {
                    "is-invalid": formik.touched.name && formik.errors.name,
                    "is-valid": formik.touched.name && !formik.errors.name,
                  })}
                  placeholder="Enter certificate name / title"
                  {...formik.getFieldProps("name")}
                />
              </div>
              <div className="col-12 mb-3">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  className={clsx("form-control textarea", {
                    "is-invalid":
                      formik.touched.description && formik.errors.description,
                    "is-valid":
                      formik.touched.description && !formik.errors.description,
                  })}
                  placeholder="Enter a short description..."
                  {...formik.getFieldProps("description")}
                  cols={30}
                  rows={10}
                ></textarea>
              </div>
              <div className="col-12 mb-3 mt-3">
                <label className="form-label fs-6">Upload Certificate </label>
                <div className="file-input">
                  <input
                    type="file"
                    multiple
                    onChange={imageHandler}
                    ref={fileInputRef}
                    accept=".jpg, .jpeg, .png, .webp, .pdf, .doc, .docx"
                  />
                  <label htmlFor="file">
                    <i className="material-symbols-outlined">upload</i>
                    <span>Upload Certificate</span>
                  </label>
                </div>
                <span className="file-info">
                  Supported file types: .jpg, .png, .jpeg, .webp, .pdf, .doc,
                  .docx. Max size: 1MB
                </span>
                {formImages.length > 0 && (
                  <div>
                    <h4>Uploaded Certificates</h4>
                    <div className="col-12 d-flex">
                      {formImages.map((file, index) => (
                        <div key={index} className="mt-2 me-2">
                          {file.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(file)}
                              height="100"
                              width="100"
                              alt="certificate-preview"
                            />
                          ) : (
                            <span>{file.name}</span> // Display file name for non-image files
                          )}
                          <button
                            type="button"
                            className="btn p-0 close d-inline-flex ms-2"
                            onClick={() => removeFile(index)}
                          >
                            <i className="material-symbols-outlined fs-18">
                              close
                            </i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {errorMessage && (
                  <div
                    style={{ color: "red" }}
                    className="custom-error-messages"
                  >
                    {errorMessage}
                    <button
                      className="btn p-0 close d-inline-flex"
                      onClick={handleErrorMessageClose}
                    >
                      <i className="material-symbols-outlined fs-18">close</i>
                    </button>
                  </div>
                )}
              </div>
              <div className="col-12 mt-3">
                <div className="row">
                  <div className="col-8 text-end">
                    <button
                      className="btn btn-cancel rounded-5"
                      onClick={clickCancel}
                    >
                      Cancel
                    </button>
                    <button
                      disabled={loading || !formik.isValid}
                      className="btn btn-outline-primary rounded-5 me-2"
                    >
                      {!loading && (
                        <span className="indicator-label">Publish</span>
                      )}
                      {loading && (
                        <span
                          className="indicator-progress"
                          style={{ display: "block" }}
                        >
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      </div>
    </section>
  );
};

export { AddCertificate };
